import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { coachById } from "@/data/coaches";
import { Mic, MicOff, Send, X, Volume2, VolumeX } from "lucide-react";
import axios from "axios";
import usePageTitle from "@/hooks/usePageTitle";
import { LogoIcon } from "@/components/brand/Logo";
import CoachAvatar from "@/components/CoachAvatar";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtTime(secs) {
  const s = Math.max(0, Math.floor(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

export default function CoachingSessionPage() {
  usePageTitle("Coaching Session");
  const { session_id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [closingPhase, setClosingPhase] = useState(null); // null | "insight" | "action" | "confidence" | "final"
  const [insight, setInsight] = useState("");
  const [action, setAction] = useState("");
  const [confidence, setConfidence] = useState(0);
  const [endedSummary, setEndedSummary] = useState(null);
  const recognitionRef = useRef(null);
  const chatEndRef = useRef(null);
  const handleAutoCloseRef = useRef(null);
  const audioRef = useRef(null);
  const playedKeysRef = useRef(new Set());
  const [voiceOn, setVoiceOn] = useState(false);
  const [voiceLoading, setVoiceLoading] = useState(false);

  // Load saved voice preference once
  useEffect(() => {
    axios.get(`${API}/api/me/voice-preference`, { withCredentials: true })
      .then((r) => setVoiceOn(!!r.data?.voice_enabled))
      .catch(() => {});
  }, []);

  const toggleVoice = async () => {
    const next = !voiceOn;
    setVoiceOn(next);
    if (!next && audioRef.current) {
      try { audioRef.current.pause(); } catch { /* noop */ }
    }
    try {
      await axios.post(`${API}/api/me/voice-preference`, { enabled: next }, { withCredentials: true });
    } catch { setVoiceOn(!next); }
  };

  // Whenever a new coach message arrives, fetch + play TTS (if voice on)
  useEffect(() => {
    if (!voiceOn || !session || messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last?.role !== "coach" || !last.content) return;
    const key = `${session_id}-${messages.length - 1}`;
    if (playedKeysRef.current.has(key)) return;
    playedKeysRef.current.add(key);
    setVoiceLoading(true);
    axios.post(`${API}/api/tts/coach-message`,
      { message_id: key, coach_id: session.coach_id, text: last.content.slice(0, 3500) },
      { withCredentials: true })
      .then((r) => {
        if (!voiceOn) return;
        const src = r.data?.audio_url ? `${API}${r.data.audio_url}` : null;
        if (!src) return;
        if (audioRef.current) { try { audioRef.current.pause(); } catch { /* noop */ } }
        const a = new Audio(src);
        audioRef.current = a;
        a.play().catch(() => { /* user-gesture blocked; UI button covers that */ });
      })
      .catch(() => { /* fail silent — voice is a non-critical aid */ })
      .finally(() => setVoiceLoading(false));
  }, [messages, voiceOn, session, session_id]);

  // Load session
  useEffect(() => {
    axios.get(`${API}/api/coaching/session/${session_id}`, { withCredentials: true })
      .then((r) => {
        setSession(r.data);
        setMessages(r.data.messages || []);
        const total = (r.data.length_minutes || 30) * 60;
        setTotalSeconds(total);
        const started = new Date(r.data.started_at).getTime();
        const elapsed = (Date.now() - started) / 1000;
        setRemaining(Math.max(0, total - elapsed));
        if (r.data.status === "completed") {
          setEndedSummary({
            coach: r.data.coach_name, topic: r.data.topic, length: r.data.length_minutes,
            insight: r.data.key_insight, action: r.data.action_committed, confidence: r.data.confidence_score,
          });
        }
      })
      .catch(() => navigate("/coaching"));
  }, [session_id]);

  // Timer
  useEffect(() => {
    if (!session || endedSummary) return;
    const t = setInterval(() => {
      setRemaining((r) => {
        const nr = r - 1;
        if (nr <= 0) {
          clearInterval(t);
          handleAutoCloseRef.current?.();
          return 0;
        }
        return nr;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [session, endedSummary]);

  // Auto-scroll
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // 90% closing prompt
  useEffect(() => {
    if (!session || endedSummary || totalSeconds === 0) return;
    const pct = (totalSeconds - remaining) / totalSeconds;
    if (pct >= 0.9 && closingPhase === null) {
      setClosingPhase("insight");
    }
  }, [remaining, totalSeconds, session, closingPhase, endedSummary]);

  const handleSend = async (overrideText) => {
    const text = (overrideText ?? draft).trim();
    if (!text || sending) return;
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: text, timestamp: new Date().toISOString() }]);
    setDraft("");
    try {
      const r = await axios.post(`${API}/api/coaching/message`, { session_id, content: text }, { withCredentials: true });
      setMessages((m) => [...m, { role: "coach", content: r.data.reply, timestamp: new Date().toISOString() }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "coach", content: "I had a moment of pause. Could you share that again?", timestamp: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  };

  // Voice input — Web Speech API
  const toggleRecording = () => {
    if (recording) {
      recognitionRef.current?.stop();
      setRecording(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Voice input is not supported in this browser. Please use text input."); return; }
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = "en-US";
    rec.onresult = (e) => {
      const t = e.results[0][0].transcript;
      setDraft((d) => (d ? d + " " : "") + t);
    };
    rec.onend = () => setRecording(false);
    rec.onerror = () => setRecording(false);
    recognitionRef.current = rec;
    rec.start();
    setRecording(true);
  };

  const handleAutoClose = async () => {
    if (endedSummary) return;
    await endSession(insight || "Time concluded", action, confidence);
  };
  // Always point the ref at the latest closure so the timer can call it
  // without us needing to re-create the timer on every keystroke.
  handleAutoCloseRef.current = handleAutoClose;

  const endSession = async (finalInsight = insight, finalAction = action, finalConf = confidence) => {
    try {
      await axios.post(`${API}/api/coaching/end`, {
        session_id, key_insight: finalInsight, action_committed: finalAction, confidence_score: finalConf,
      }, { withCredentials: true });
    } catch (err) { console.error(err); }
    setEndedSummary({
      coach: session?.coach_name, topic: session?.topic, length: session?.length_minutes,
      insight: finalInsight, action: finalAction, confidence: finalConf,
    });
  };

  if (!session) return <div className="min-h-screen bg-cream flex items-center justify-center"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  // Post-session summary
  if (endedSummary) {
    const coach = coachById(session.coach_id);
    return (
      <div className="min-h-screen bg-cream pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="post-session-summary">
          <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center font-bold text-2xl" style={{ backgroundColor: coach?.bg, color: coach?.fg }}>{coach?.initials}</div>
          <h1 className="font-playfair text-navy text-3xl font-bold mb-2">Session complete</h1>
          <p className="font-dm text-navy/60 text-sm mb-6">{endedSummary.coach} · {endedSummary.topic} · {endedSummary.length} minutes</p>
          <div className="text-left space-y-4 mb-6">
            <div><p className="font-dm text-navy/40 text-xs uppercase font-bold mb-1">Key insight</p><p className="font-dm text-navy text-sm italic border-l-2 border-gold pl-3">{endedSummary.insight || "—"}</p></div>
            <div><p className="font-dm text-navy/40 text-xs uppercase font-bold mb-1">Action committed</p><p className="font-dm text-navy text-sm">{endedSummary.action || "—"}</p></div>
            <div><p className="font-dm text-navy/40 text-xs uppercase font-bold mb-1">Confidence</p><div className="flex items-center gap-2"><div className="flex-1 h-3 bg-navy/10 rounded-full overflow-hidden"><div className="h-full bg-gold" style={{ width: `${endedSummary.confidence * 10}%` }} /></div><span className="font-dm text-navy text-sm font-bold">{endedSummary.confidence}/10</span></div></div>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            <button onClick={() => navigate("/coaching/start")} data-testid="start-another-btn" className="bg-gold text-navy font-dm font-bold px-6 py-2.5 rounded-lg">Start Another Session</button>
            <button onClick={() => navigate("/coaching/dashboard")} data-testid="view-dashboard-btn" className="border border-navy text-navy font-dm font-bold px-6 py-2.5 rounded-lg hover:bg-navy hover:text-gold">View in Dashboard</button>
          </div>
        </div>
      </div>
    );
  }

  const coach = coachById(session.coach_id);
  const pct = totalSeconds > 0 ? (totalSeconds - remaining) / totalSeconds : 0;
  const timerColour = pct >= 0.9 ? "text-red-500" : pct >= 0.75 ? "text-amber-500" : "text-gold";

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      {/* Top bar */}
      <div className="bg-navy px-4 py-3 flex items-center justify-between gap-3 sticky top-16 z-30" data-testid="session-topbar">
        <div className="flex items-center gap-3 min-w-0">
          <LogoIcon width={28} height={28} />
          <CoachAvatar coach={coach} size={40} ring />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-dm text-white text-sm font-bold truncate">{session.coach_name}</p>
              <span className="font-dm text-[9px] text-gold/80 uppercase tracking-[0.15em] flex-shrink-0">AI Coach</span>
            </div>
            <p className="font-dm text-white/60 text-xs truncate">{session.topic}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {session.free_session && <span className="hidden sm:inline-block bg-green-500 text-white font-dm text-[10px] font-bold px-2 py-0.5 rounded-full">FREE</span>}
          <button onClick={toggleVoice} data-testid="voice-toggle-btn"
            title={voiceOn ? "Mute coach voice" : "Hear coach voice"}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full font-dm text-xs font-bold transition-colors ${voiceOn ? "bg-gold text-navy" : "bg-white/10 text-white/70 hover:bg-white/20"}`}>
            {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            <span className="hidden sm:inline">{voiceLoading ? "…" : voiceOn ? "Voice" : "Mute"}</span>
          </button>
          <span className={`font-mono font-bold text-lg ${timerColour}`} data-testid="session-timer">{fmtTime(remaining)}</span>
          <button onClick={() => setClosingPhase("insight")} data-testid="end-session-btn" className="text-white/70 hover:text-red-400 font-dm text-xs"><X className="w-5 h-5" /></button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-navy/10">
        <div className={`h-full transition-all ${pct >= 0.9 ? "bg-red-500" : pct >= 0.75 ? "bg-amber-500" : "bg-gold"}`} style={{ width: `${pct * 100}%` }} />
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6 max-w-3xl mx-auto w-full" data-testid="session-messages">
        {messages.map((m, i) => (
          <div key={i} className={`mb-4 flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "coach" && <CoachAvatar coach={coach} size={28} className="mb-1" />}
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-navy text-white" : "bg-white border-l-4 text-navy shadow-sm"}`} style={m.role === "coach" ? { borderLeftColor: coach?.fg } : {}}>
              <p className="font-dm text-sm leading-relaxed whitespace-pre-wrap">{m.content}</p>
            </div>
          </div>
        ))}
        {sending && (
          <div className="mb-4 flex items-end gap-2 justify-start">
            <CoachAvatar coach={coach} size={28} className="mb-1" />
            <div className="bg-white border-l-4 rounded-2xl px-4 py-3 shadow-sm" style={{ borderLeftColor: coach?.fg }}>
              <div className="flex gap-1"><span className="w-2 h-2 bg-navy/30 rounded-full animate-pulse" /><span className="w-2 h-2 bg-navy/30 rounded-full animate-pulse" style={{ animationDelay: "0.2s" }} /><span className="w-2 h-2 bg-navy/30 rounded-full animate-pulse" style={{ animationDelay: "0.4s" }} /></div>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input bar */}
      <div className="bg-white border-t border-navy/10 px-4 py-3 sticky bottom-0">
        <div className="max-w-3xl mx-auto flex items-end gap-2">
          <button onClick={toggleRecording} disabled={sending} data-testid="voice-btn"
            className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all ${recording ? "bg-red-500 text-white animate-pulse" : "bg-navy/5 text-navy hover:bg-navy/10"}`}>
            {recording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder="Share your thoughts..." rows={1} data-testid="message-input"
            className="flex-1 border border-navy/20 rounded-2xl px-4 py-2.5 font-dm text-sm resize-none focus:border-gold focus:outline-none max-h-32" />
          <button onClick={() => handleSend()} disabled={!draft.trim() || sending} data-testid="send-btn"
            className="flex-shrink-0 w-11 h-11 rounded-full bg-gold text-navy flex items-center justify-center disabled:opacity-50">
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Closing modal — captures insight/action/confidence */}
      {closingPhase && !endedSummary && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7" data-testid="closing-modal">
            <h2 className="font-playfair text-navy text-xl font-bold mb-1">Before we close</h2>
            <p className="font-dm text-navy/60 text-sm mb-4">Take a moment to anchor the session.</p>

            <label className="font-dm text-navy text-xs font-semibold block mb-1">What is the most important insight that emerged for you?</label>
            <textarea value={insight} onChange={(e) => setInsight(e.target.value)} placeholder="The thing I take with me…" rows={2} data-testid="insight-input"
              className="w-full border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none mb-3" />

            <label className="font-dm text-navy text-xs font-semibold block mb-1">What action are you committing to before we next speak?</label>
            <input value={action} onChange={(e) => setAction(e.target.value)} placeholder="One specific action…" data-testid="action-input"
              className="w-full border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none mb-3" />

            <label className="font-dm text-navy text-xs font-semibold block mb-1">How confident are you that you will take this action? ({confidence}/10)</label>
            <input type="range" min="0" max="10" value={confidence} onChange={(e) => setConfidence(Number(e.target.value))} data-testid="confidence-slider" className="w-full mb-5 accent-gold" />

            <div className="flex gap-3">
              <button onClick={() => setClosingPhase(null)} className="font-dm text-navy/60 px-4 py-2.5">Keep going</button>
              <button onClick={() => endSession()} data-testid="close-session-btn" className="flex-1 bg-navy text-gold font-dm font-bold py-2.5 rounded-lg">Close Session</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
